import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSEO } from '@/hooks/useSEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  User, Lock, Bell, AlertTriangle, Check, X, Eye, EyeOff,
  Settings as SettingsIcon, Loader2,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const inputStyle = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

const sectionStyle = {
  background: 'var(--l1, rgba(255,255,255,0.03))',
  border: '1px solid var(--b1)',
  borderRadius: 10,
  padding: '1.5rem',
  marginBottom: '1.25rem',
};

const sectionTitleStyle = {
  fontFamily: 'DM Sans, sans-serif',
  fontWeight: 600,
  fontSize: '1rem',
  color: 'var(--white)',
  marginBottom: '0.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const sectionDescStyle = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.82rem',
  color: 'var(--ts)',
  marginBottom: '1rem',
};

function Toast({ kind, message, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  const isError = kind === 'error';
  return (
    <div
      data-testid="settings-toast"
      data-kind={kind}
      style={{
        position: 'fixed', bottom: '1.5rem', right: '1.5rem',
        background: 'rgba(10,8,20,0.98)',
        border: `1px solid ${isError ? 'var(--danger)' : 'var(--ok)'}`,
        borderRadius: 10, padding: '0.75rem 1.1rem',
        zIndex: 1100, maxWidth: 320,
        boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
      }}
    >
      <p style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
        fontWeight: 600, color: 'var(--white)',
        display: 'flex', alignItems: 'center', gap: '0.5rem',
      }}>
        {isError ? <AlertTriangle size={14} style={{ color: 'var(--danger)' }} /> : <Check size={14} style={{ color: 'var(--ok)' }} />}
        {message}
      </p>
    </div>
  );
}

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: 'var(--ts)' };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[a-z]/.test(pw)) score += 1;
  if (/[0-9]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  const labels = ['', 'Very weak', 'Weak', 'Okay', 'Strong', 'Very strong'];
  const colors = ['var(--ts)', 'var(--danger)', 'var(--danger)', 'var(--warn)', 'var(--ok)', 'var(--ok)'];
  return { score, label: labels[score], color: colors[score] };
}

export default function SettingsPage() {
  useSEO({
    title: 'Settings | Acodia',
    description: 'Manage your Acodia account, password and notification preferences.',
    path: '/dashboard/settings',
    robots: 'noindex,nofollow',
  });

  const { user, authFetch, logout } = useAuth();
  const [toast, setToast] = useState(null);

  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
  }, []);

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)', color: 'var(--white)' }}>
      <SettingsSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="sticky top-0 z-20 flex items-center px-6 h-[68px]"
          style={{
            background: 'var(--l1)',
            borderBottom: '1px solid var(--b1)',
          }}
        >
          <h1 style={{
            fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '1.1rem',
            color: 'var(--white)', display: 'flex', alignItems: 'center', gap: '0.55rem',
          }}>
            <SettingsIcon size={18} style={{ color: 'var(--a)' }} />
            Settings
          </h1>
        </header>

        <main
          data-testid="settings-content"
          className="flex-1 overflow-y-auto p-7"
          style={{ background: 'var(--bg)', maxWidth: 760, width: '100%' }}
        >
          <AccountDetailsSection user={user} authFetch={authFetch} showToast={showToast} />
          <PasswordSection authFetch={authFetch} showToast={showToast} />
          <NotificationsSection authFetch={authFetch} showToast={showToast} />
          <DangerZoneSection authFetch={authFetch} logout={logout} showToast={showToast} />
        </main>
      </div>

      {toast && (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}

/* ── Slim sidebar — links back to the dashboard plus a Settings active row. ── */
function SettingsSidebar() {
  return (
    <aside
      data-testid="settings-sidebar"
      className="hidden lg:flex flex-col w-60 border-r"
      style={{
        background: 'var(--l1)',
        borderColor: 'var(--b1)',
        minHeight: '100vh',
      }}
    >
      <div className="h-[68px] flex items-center px-6 border-b" style={{ borderColor: 'var(--b1)' }}>
        <a href="/" className="flex items-center gap-2" style={{ color: 'var(--white)' }}>
          <div className="relative w-2 h-2">
            <div className="absolute inset-0 rounded-full animate-pulse-dot" style={{ background: 'var(--a)' }} />
          </div>
          <span className="font-display text-base font-bold">acodia</span>
        </a>
      </div>
      <nav className="flex-1 py-6 px-3">
        <a
          href="/dashboard"
          className="flex items-center gap-3 px-3 py-2.5 rounded-[3px] transition-all"
          style={{ color: 'var(--ts)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--white)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ts)'; e.currentTarget.style.background = 'transparent'; }}
        >
          <span className="font-body text-sm font-medium">← Back to Dashboard</span>
        </a>

        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-[3px] mt-2"
          style={{
            color: 'var(--white)',
            background: 'var(--a-glow, rgba(139,92,246,0.12))',
            borderLeft: '2px solid var(--a)',
          }}
        >
          <SettingsIcon size={16} />
          <span className="font-body text-sm font-medium">Settings</span>
        </div>
      </nav>
    </aside>
  );
}

/* ───────────────────── Account Details ───────────────────── */

function AccountDetailsSection({ user, authFetch, showToast }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFirstName(user?.first_name || '');
    setLastName(user?.last_name || '');
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      showToast('error', 'Please fill in both names');
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName.trim(), last_name: lastName.trim() }),
      });
      if (!res.ok) throw new Error('Save failed');
      showToast('success', 'Profile updated');
    } catch (err) {
      showToast('error', err?.message || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="settings-account" style={sectionStyle}>
      <p style={sectionTitleStyle}><User size={15} /> Account Details</p>
      <p style={sectionDescStyle}>Your name as it appears on Acodia reports and emails.</p>

      <form onSubmit={handleSave}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
          <Input
            data-testid="settings-first-name"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 42 }}
          />
          <Input
            data-testid="settings-last-name"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 42 }}
          />
        </div>

        <div style={{ marginTop: '0.65rem' }}>
          <Input
            data-testid="settings-email"
            value={user?.email || ''}
            readOnly
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 42, color: 'var(--ts)', cursor: 'not-allowed' }}
          />
          <a
            href="mailto:support@acodia.co.uk?subject=Change%20email%20address"
            data-testid="settings-change-email"
            style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem',
              color: 'var(--a)', marginTop: '0.3rem', display: 'inline-block',
            }}
          >
            Change email
          </a>
        </div>

        <Button
          type="submit"
          data-testid="settings-account-save"
          disabled={saving}
          className="btn-shimmer"
          style={{
            background: 'var(--a)', color: '#fff', marginTop: '1rem',
            height: 42, padding: '0 1.25rem', borderRadius: 3,
            fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? (
            <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
              <Loader2 size={14} style={{ animation: 'scannerSpin 1s linear infinite' }} />
              Saving...
            </span>
          ) : 'Save changes'}
        </Button>
      </form>
    </section>
  );
}

/* ───────────────────── Password ───────────────────── */

function PasswordSection({ authFetch, showToast }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const strength = passwordStrength(next);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!current) { showToast('error', 'Enter your current password'); return; }
    if (next.length < 8) { showToast('error', 'New password must be at least 8 characters'); return; }
    if (next !== confirm) { showToast('error', 'New passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/user/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.detail === 'string'
          ? data.detail
          : (data?.detail?.error || 'Could not update password');
        throw new Error(msg);
      }
      setCurrent(''); setNext(''); setConfirm('');
      showToast('success', 'Password updated');
    } catch (err) {
      showToast('error', err?.message || 'Could not update password');
    } finally {
      setSaving(false);
    }
  };

  const passwordField = (value, onChange, placeholder, testId) => (
    <div style={{ position: 'relative' }}>
      <Input
        data-testid={testId}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-[3px]"
        style={{ ...inputStyle, height: 42, paddingRight: 38 }}
      />
      <button
        type="button"
        aria-label={show ? 'Hide passwords' : 'Show passwords'}
        onClick={() => setShow((s) => !s)}
        style={{
          position: 'absolute', right: 10, top: 0, bottom: 0,
          background: 'none', border: 'none', color: 'var(--ts)',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
        }}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );

  return (
    <section data-testid="settings-password" style={sectionStyle}>
      <p style={sectionTitleStyle}><Lock size={15} /> Change Password</p>
      <p style={sectionDescStyle}>Use a strong password with letters, numbers and symbols.</p>

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {passwordField(current, setCurrent, 'Current password', 'settings-current-password')}
        {passwordField(next, setNext, 'New password (8+ characters)', 'settings-new-password')}
        {next && (
          <div style={{ marginTop: '-0.2rem' }}>
            <div style={{
              height: 4, borderRadius: 2,
              background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
            }}>
              <div style={{
                width: `${(strength.score / 5) * 100}%`, height: '100%',
                background: strength.color, transition: 'width 200ms ease',
              }} />
            </div>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem',
              color: strength.color, marginTop: '0.3rem',
            }}>
              {strength.label}
            </p>
          </div>
        )}
        {passwordField(confirm, setConfirm, 'Confirm new password', 'settings-confirm-password')}

        <Button
          type="submit"
          data-testid="settings-password-save"
          disabled={saving}
          className="btn-shimmer"
          style={{
            background: 'var(--a)', color: '#fff', marginTop: '0.4rem',
            height: 42, padding: '0 1.25rem', borderRadius: 3,
            fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
            alignSelf: 'flex-start',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save password'}
        </Button>
      </form>
    </section>
  );
}

/* ───────────────────── Notifications ───────────────────── */

function NotificationsSection({ authFetch, showToast }) {
  const [prefs, setPrefs] = useState({
    weekly_security_report: true,
    realtime_critical_alerts: true,
    certificate_expiry_warnings: true,
    breach_detection_alerts: true,
    weekly_digest: true,
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/user/notifications`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          if (data?.notification_prefs) setPrefs(data.notification_prefs);
        }
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/user/notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs),
      });
      if (!res.ok) throw new Error('Save failed');
      showToast('success', 'Preferences saved');
    } catch (err) {
      showToast('error', err?.message || 'Could not save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleStyle = (on) => ({
    width: 38, height: 22, borderRadius: 999, cursor: 'pointer',
    background: on ? 'var(--a)' : 'rgba(255,255,255,0.1)',
    border: 'none', padding: 0, position: 'relative',
    transition: 'background 150ms ease',
  });
  const thumbStyle = (on) => ({
    width: 16, height: 16, borderRadius: '50%', background: '#fff',
    position: 'absolute', top: 3, left: on ? 19 : 3,
    transition: 'left 150ms ease',
  });

  const Row = ({ id, label, prefKey }) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0.6rem 0', borderTop: '1px solid var(--b1)',
    }}>
      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', color: 'var(--white)' }}>
        {label}
      </span>
      <button
        type="button"
        aria-pressed={prefs[prefKey]}
        data-testid={id}
        onClick={() => setPrefs((p) => ({ ...p, [prefKey]: !p[prefKey] }))}
        style={toggleStyle(prefs[prefKey])}
      >
        <span style={thumbStyle(prefs[prefKey])} />
      </button>
    </div>
  );

  return (
    <section data-testid="settings-notifications" style={sectionStyle}>
      <p style={sectionTitleStyle}><Bell size={15} /> Notifications</p>
      <p style={sectionDescStyle}>Choose which security events trigger an email.</p>

      <div>
        <Row id="settings-pref-weekly" label="Weekly security report" prefKey="weekly_security_report" />
        <Row id="settings-pref-digest" label="Weekly email digest" prefKey="weekly_digest" />
        <Row id="settings-pref-realtime" label="Real-time critical alerts" prefKey="realtime_critical_alerts" />
        <Row id="settings-pref-cert" label="Certificate expiry warnings" prefKey="certificate_expiry_warnings" />
        <Row id="settings-pref-breach" label="New breach detections" prefKey="breach_detection_alerts" />
      </div>

      <Button
        type="button"
        data-testid="settings-notifications-save"
        onClick={handleSave}
        disabled={!loaded || saving}
        className="btn-shimmer"
        style={{
          background: 'var(--a)', color: '#fff', marginTop: '1rem',
          height: 42, padding: '0 1.25rem', borderRadius: 3,
          fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
          opacity: !loaded || saving ? 0.7 : 1,
        }}
      >
        {saving ? 'Saving...' : 'Save preferences'}
      </Button>
    </section>
  );
}

/* ───────────────────── Danger Zone ───────────────────── */

function DangerZoneSection({ authFetch, logout, showToast }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const confirmed = confirmText.trim() === 'DELETE';

  const handleDelete = async () => {
    if (!confirmed) return;
    setDeleting(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/user`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      await logout();
      window.location.href = '/';
    } catch (err) {
      showToast('error', err?.message || 'Could not delete account');
      setDeleting(false);
    }
  };

  return (
    <section
      data-testid="settings-danger"
      style={{
        ...sectionStyle,
        border: '1px solid rgba(220,38,38,0.3)',
      }}
    >
      <p style={{
        ...sectionTitleStyle,
        color: 'var(--danger)',
      }}>
        <AlertTriangle size={15} /> Danger Zone
      </p>
      <p style={sectionDescStyle}>
        This will permanently delete your account and all data. This cannot be undone.
      </p>

      <Button
        type="button"
        data-testid="settings-delete-account"
        onClick={() => setOpen(true)}
        style={{
          background: 'transparent',
          border: '1px solid var(--danger)',
          color: 'var(--danger)',
          height: 42, padding: '0 1.25rem', borderRadius: 3,
          fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
        }}
      >
        Delete Account
      </Button>

      {open && (
        <div
          data-testid="settings-delete-modal"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem',
          }}
          onClick={() => !deleting && setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(10,8,20,0.98)',
              border: '1px solid rgba(220,38,38,0.4)',
              borderRadius: 16, padding: '2rem',
              maxWidth: 460, width: '100%',
              boxShadow: '0 0 60px rgba(220,38,38,0.15)',
              position: 'relative',
            }}
          >
            <button
              type="button"
              onClick={() => !deleting && setOpen(false)}
              aria-label="Close"
              style={{
                position: 'absolute', top: '1rem', right: '1rem',
                background: 'none', border: 'none', color: 'var(--ts)',
                cursor: 'pointer', padding: 4, lineHeight: 0,
              }}
            >
              <X size={18} />
            </button>

            <p style={{
              fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '1.1rem',
              color: 'var(--danger)',
            }}>
              Delete account
            </p>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
              color: 'var(--tm)', marginTop: '0.5rem', lineHeight: 1.55,
            }}>
              This action is permanent. Your account, subscription, domains and scan
              history will all be removed and cannot be recovered.
            </p>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem',
              color: 'var(--ts)', marginTop: '1rem',
            }}>
              Type <span style={{ color: 'var(--white)', fontWeight: 600 }}>DELETE</span> to confirm:
            </p>
            <Input
              data-testid="settings-delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="rounded-[3px]"
              style={{ ...inputStyle, height: 42, marginTop: '0.4rem' }}
              autoFocus
            />

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <Button
                type="button"
                onClick={() => setOpen(false)}
                disabled={deleting}
                style={{
                  flex: 1, background: 'transparent',
                  border: '1px solid var(--b1)', color: 'var(--white)',
                  height: 42, borderRadius: 3, fontFamily: 'DM Sans, sans-serif',
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="settings-delete-confirm-button"
                onClick={handleDelete}
                disabled={!confirmed || deleting}
                style={{
                  flex: 1,
                  background: confirmed ? 'var(--danger)' : 'rgba(220,38,38,0.3)',
                  color: '#fff', height: 42, borderRadius: 3,
                  fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                  opacity: deleting ? 0.7 : 1,
                  cursor: confirmed && !deleting ? 'pointer' : 'not-allowed',
                }}
              >
                {deleting ? 'Deleting...' : 'Delete forever'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
